import React, { useState } from 'react';
import api from '../../services/api';
import { hojeBR, vencimentoParcela } from '../../utils/data';
import { navegarComEnter } from '../../utils/enterNavegacao';
import s from '../../pages/PedidosOrcamentos.module.css';

const hoje = () => hojeBR();

/**
 * Lançamento manual de título (avulso) com PARCELAMENTO — usado pelo F4/Novo
 * de Contas a Pagar (tipo='pagar', entidade=fornecedor) e Contas a Receber
 * (tipo='receber', entidade=cliente). Gera uma conta por parcela (i/n) com a
 * regra de vencimento escolhida; centavos de arredondamento vão na última.
 */
export default function ModalNovoTituloParcelado({
  tipo, entidades, contasFinanc, planoContas, onSalvo, onClose,
}) {
  const ehPagar = tipo === 'pagar';
  const rotuloEntidade = ehPagar ? 'Fornecedor' : 'Cliente';

  const [f, setF] = useState({
    entidade_id: '', valor_total: '', parcelas: '1',
    data_emissao: hoje(), venc1: hoje(),
    regra: 'direto', n_dias: '',
    plano_conta_id: '', conta_id: '', numero_documento: '',
    descricao: '', observacoes: '',
  });
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const qtd   = Math.max(1, parseInt(f.parcelas) || 1);
  const total = parseFloat(String(f.valor_total).replace(',', '.')) || 0;

  // Prévia dos vencimentos/valores (mesma conta usada no salvar)
  const parcelas = [];
  if (total > 0 && f.venc1) {
    const base = Math.floor((total / qtd) * 100) / 100;
    for (let i = 0; i < qtd; i++) {
      parcelas.push({
        n: i + 1,
        valor: i === qtd - 1 ? +(total - base * (qtd - 1)).toFixed(2) : base,
        vencimento: vencimentoParcela(f.venc1, i, f.regra, f.n_dias),
      });
    }
  }

  const confirmar = async () => {
    if (!f.entidade_id)  return setErro(`Informe o ${rotuloEntidade.toLowerCase()}.`);
    if (!total || total <= 0) return setErro('Informe o valor total.');
    if (!f.venc1)        return setErro('Informe o vencimento da 1ª parcela.');
    if (f.regra === 'ndias' && (!parseInt(f.n_dias) || parseInt(f.n_dias) <= 0))
      return setErro('Informe quantos dias entre as parcelas.');
    if (!f.descricao.trim()) return setErro('Informe a descrição.');
    setErro('');
    setSalvando(true);
    try {
      const endpoint = ehPagar ? '/financeiro/contas-pagar' : '/financeiro/contas-receber';
      let primeiroId = null;
      for (const p of parcelas) {
        const { data } = await api.post(endpoint, {
          descricao: qtd > 1 ? `${f.descricao.trim()} (${p.n}/${qtd})` : f.descricao.trim(),
          valor: p.valor,
          numero_documento: f.numero_documento || null,
          parcela: p.n,
          total_parcelas: qtd,
          data_emissao: f.data_emissao || hoje(),
          data_vencimento: p.vencimento,
          [ehPagar ? 'fornecedor_id' : 'cliente_id']: f.entidade_id,
          conta_id: f.conta_id || null,
          plano_conta_id: f.plano_conta_id || null,
          observacoes: f.observacoes || null,
          status: 'aberto',
        });
        if (!primeiroId) primeiroId = data.id;
      }
      onSalvo(primeiroId);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao salvar as parcelas.');
    } finally {
      setSalvando(false);
    }
  };

  const lbl = { display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12 };
  const fmt = v => Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const fmtD = iso => new Date(iso + 'T12:00:00').toLocaleDateString('pt-BR');

  return (
    <div className={s.confirmOverlay}>
      <div className={s.confirmBox} style={{ width: 640 }} data-enter-scope="on" onKeyDown={navegarComEnter}>
        <div className={s.confirmTitulo}>
          {ehPagar ? 'Nova Conta a Pagar (F4)' : 'Nova Conta a Receber (F4)'}
        </div>
        <div className={s.confirmCorpo}>
          {erro && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 4,
              padding: '6px 10px', fontSize: 12, color: '#b91c1c', marginBottom: 10 }}>
              {erro}
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={lbl}>
              {rotuloEntidade} *
              <select className={s.selectErp} autoFocus value={f.entidade_id}
                onChange={e => {
                  const id = e.target.value;
                  // Puxa o plano de contas padrão do fornecedor (cadastro
                  // de Fornecedores) — sempre que troca de fornecedor,
                  // nunca sobrescreve depois disso: o usuário continua
                  // livre pra trocar manualmente o select de categoria.
                  if (ehPagar) {
                    const fornecedor = entidades.find(en => String(en.id) === String(id));
                    const plano = fornecedor?.plano_conta_codigo
                      ? planoContas.find(pc => pc.codigo === fornecedor.plano_conta_codigo)
                      : null;
                    setF(p => ({ ...p, entidade_id: id, plano_conta_id: plano?.id || p.plano_conta_id }));
                  } else {
                    set('entidade_id', id);
                  }
                }}>
                <option value="">— selecione —</option>
                {entidades.map(en => (
                  <option key={en.id} value={en.id}>
                    {en.nome_fantasia || en.razao_social || en.nome}
                  </option>
                ))}
              </select>
            </label>

            <div style={{ display: 'flex', gap: 8 }}>
              <label style={{ ...lbl, flex: 1 }}>
                Valor Total R$ *
                <input type="number" step="0.01" min="0.01" className={s.inputErp}
                  value={f.valor_total} onChange={e => set('valor_total', e.target.value)} />
              </label>
              <label style={{ ...lbl, width: 110 }}>
                Qtd. Parcelas
                <input type="number" min="1" max="120" className={s.inputErp}
                  value={f.parcelas} onChange={e => set('parcelas', e.target.value)} />
              </label>
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <label style={{ ...lbl, flex: 1 }}>
                Emissão
                <input type="date" className={s.inputErp} value={f.data_emissao}
                  onChange={e => set('data_emissao', e.target.value)} />
              </label>
              <label style={{ ...lbl, flex: 1 }}>
                Vencimento da 1ª *
                <input type="date" className={s.inputErp} value={f.venc1}
                  onChange={e => set('venc1', e.target.value)} />
              </label>
            </div>

            {/* Vencimento das Parcelas — mesmas opções do sistema antigo */}
            {qtd > 1 && (
              <fieldset style={{ border: '1px solid #93c5fd', borderRadius: 4, padding: '6px 10px' }}>
                <legend style={{ fontSize: 11.5, color: '#1e3a5f', padding: '0 4px' }}>Vencimento das Parcelas</legend>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input type="radio" name="regraVenc" checked={f.regra === 'direto'}
                      onChange={() => set('regra', 'direto')} />
                    Somar 30 Dias Direto
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input type="radio" name="regraVenc" checked={f.regra === 'mes'}
                      onChange={() => set('regra', 'mes')} />
                    Mesmo dia de Cada Mês
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input type="radio" name="regraVenc" checked={f.regra === 'ndias'}
                      onChange={() => set('regra', 'ndias')} />
                    A cada
                    <input type="number" min="1" style={{ width: 52, padding: '2px 4px', fontSize: 12,
                      border: '1px solid #ccc', borderRadius: 3, color: '#b91c1c', fontWeight: 700 }}
                      value={f.n_dias}
                      onFocus={() => set('regra', 'ndias')}
                      onChange={e => set('n_dias', e.target.value)} />
                    dias
                  </label>
                </div>
              </fieldset>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <label style={{ ...lbl, flex: 1 }}>
                Categoria (Plano de Contas)
                <select className={s.selectErp} value={f.plano_conta_id}
                  onChange={e => set('plano_conta_id', e.target.value)}>
                  <option value="">— Nenhuma —</option>
                  {planoContas.map(pc => (
                    <option key={pc.id} value={pc.id}>{pc.codigo} — {pc.descricao}</option>
                  ))}
                </select>
              </label>
              <label style={{ ...lbl, width: 130 }}>
                Nº Documento
                <input className={s.inputErp} value={f.numero_documento}
                  onChange={e => set('numero_documento', e.target.value)} />
              </label>
              <label style={{ ...lbl, flex: 1 }}>
                Conta Financeira (previsão)
                <select className={s.selectErp} value={f.conta_id}
                  onChange={e => set('conta_id', e.target.value)}>
                  <option value="">— Nenhuma —</option>
                  {contasFinanc.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
              </label>
            </div>

            <label style={lbl}>
              Descrição *
              <input className={s.inputErp} value={f.descricao}
                placeholder={qtd > 1 ? 'Cada parcela recebe o sufixo (1/N), (2/N)...' : ''}
                onChange={e => set('descricao', e.target.value)} />
            </label>
            <label style={lbl}>
              Observações
              <input className={s.inputErp} value={f.observacoes}
                onChange={e => set('observacoes', e.target.value)} />
            </label>

            {/* Prévia das parcelas */}
            {parcelas.length > 1 && (
              <div style={{ fontSize: 11.5, background: '#f8fafc', border: '1px solid #e2e8f0',
                borderRadius: 4, padding: '6px 10px', maxHeight: 96, overflowY: 'auto' }}>
                {parcelas.map(p => (
                  <div key={p.n} style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Parcela {p.n}/{qtd} — venc. {fmtD(p.vencimento)}</span>
                    <strong>{fmt(p.valor)}</strong>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className={s.confirmBtns}>
          <button className={`${s.btnErp} ${s.btnErpPrimario}`} data-enter-final onClick={confirmar} disabled={salvando}>
            {salvando ? 'Salvando...' : qtd > 1 ? `Gerar ${qtd} parcela(s)` : 'Salvar'}
          </button>
          <button className={s.btnErp} onClick={onClose} disabled={salvando}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}
